import { useEffect, useState } from 'react'

const MIN_HOURS = 0
const MAX_HOURS = 16
const STEP = 0.5

export interface DriveMaxFieldProps {
  /** Persisted household.max_drive_hours, or null when unset. */
  value: number | null
  disabled?: boolean
  onChange: (hours: number) => void
}

function clamp(n: number): number {
  return Math.min(MAX_HOURS, Math.max(MIN_HOURS, Math.round(n / STEP) * STEP))
}

function format(hours: number): string {
  const rounded = Math.round(hours * 2) / 2
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} hour${rounded === 1 ? '' : 's'}`
}

export function DriveMaxField({ value, disabled = false, onChange }: DriveMaxFieldProps) {
  const [draft, setDraft] = useState<number | null>(value)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const shown = draft ?? 4

  function commit(next: number) {
    const v = clamp(next)
    setDraft(v)
    onChange(v)
  }

  return (
    <div>
      <p className="spz-readout" aria-live="polite">
        {draft === null ? <span className="spz-muted">Not set yet</span> : format(draft)}
      </p>

      <div className="spz-row spz-row-tight">
        <button
          type="button"
          className="spz-step"
          aria-label="Less driving time"
          disabled={disabled || shown <= MIN_HOURS}
          onClick={() => commit(shown - STEP)}
        >
          &minus;
        </button>

        <input
          type="range"
          className="spz-range"
          min={MIN_HOURS}
          max={MAX_HOURS}
          step={STEP}
          value={shown}
          disabled={disabled}
          aria-label="Maximum comfortable driving time in hours"
          onChange={(e) => commit(Number(e.target.value))}
        />

        <button
          type="button"
          className="spz-step"
          aria-label="More driving time"
          disabled={disabled || shown >= MAX_HOURS}
          onClick={() => commit(shown + STEP)}
        >
          +
        </button>
      </div>

      <p className="spz-hint">0 to 16 hours, in half-hour steps. One way.</p>
    </div>
  )
}

export default DriveMaxField
